import { UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { useRouter } from "next/compat/router";
import posthog from "posthog-js";
import { useEffect, useRef } from "react";

import { useAccess, useUserAction } from "~/hooks";
import useConversionFlag from "~/hooks/useConversionFlag";
import useOnceInView from "~/hooks/useOnceInView";
import useTrackCTA from "~/hooks/useTrackCTA";
import {
	CONVERSION_FLAGS,
	isConversionFlagOn,
} from "~/lib/conversion-test-flags";

type ContentMembershipContentType = "ARTICLE" | "GLOSSARY";

type ContentMembershipCTAProps = {
	contentType: ContentMembershipContentType;
	contentId: string;
	/** 0-based index when multiple mid-content CTAs are rendered. */
	placementIndex?: number;
};

const CTA_SOURCE: Record<ContentMembershipContentType, string> = {
	ARTICLE: "Article Mid Content",
	GLOSSARY: "Glossary Mid Content",
};

/**
 * Mid-content membership prompt for SEO article/glossary pages.
 * Primary path pushes paid membership; free signup is secondary for guests.
 */
const ContentMembershipCTA = ({
	contentType,
	contentId,
	placementIndex = 0,
}: ContentMembershipCTAProps) => {
	const { isLoaded, isSignedIn } = useAuth();
	const { isMember, isLoadingAccess, isPaused } = useAccess();
	const { setUserAction } = useUserAction();
	const trackCTA = useTrackCTA();
	const router = useRouter();
	const hasTrackedShown = useRef(false);
	const asideRef = useRef<HTMLElement>(null);

	const source = CTA_SOURCE[contentType];
	const authState = !isSignedIn
		? "signed_out"
		: isPaused
			? "paused"
			: "non_member";

	useEffect(() => {
		if (!isLoaded || isLoadingAccess || isMember || hasTrackedShown.current) {
			return;
		}

		hasTrackedShown.current = true;
		posthog.capture("cta:content_membership_shown", {
			content_type: contentType,
			content_id: contentId,
			auth_state: authState,
			placement_index: placementIndex,
		});
	}, [
		authState,
		contentId,
		contentType,
		isLoaded,
		isLoadingAccess,
		isMember,
		placementIndex,
	]);

	const isRendered = isLoaded && !isLoadingAccess && !isMember;
	const { value: trackingFlag } = useConversionFlag(
		CONVERSION_FLAGS.conversionTracking,
		{ enabled: isRendered },
	);
	useOnceInView(
		asideRef,
		() =>
			posthog.capture("cta:content_membership_viewed", {
				content_type: contentType,
				content_id: contentId,
				auth_state: authState,
				placement_index: placementIndex,
			}),
		isRendered && isConversionFlagOn(trackingFlag),
	);

	if (!isRendered) {
		return null;
	}

	const captureClick = (ctaRole: "primary" | "secondary") => {
		posthog.capture("cta:content_membership_clicked", {
			content_type: contentType,
			content_id: contentId,
			auth_state: authState,
			cta_role: ctaRole,
			placement_index: placementIndex,
		});
	};

	const startMembership = () => {
		captureClick("primary");
		trackCTA(UserAction.BECOME_A_MEMBER, source);

		if (isSignedIn) {
			setUserAction(UserAction.BECOME_A_MEMBER);
			return;
		}

		router?.push("/become-a-member");
	};

	const startFreeAccount = () => {
		captureClick("secondary");
		trackCTA(UserAction.SIGN_UP, source);
		setUserAction(UserAction.SIGN_UP);
	};

	return (
		<aside
			ref={asideRef}
			className="my-8 w-full overflow-hidden rounded-2xl border-2 border-sky-600 bg-linear-to-br from-sky-600 via-sky-700 to-sky-900 px-5 py-6 text-white shadow-lg shadow-sky-900/25 sm:my-10 sm:px-7 sm:py-7 dark:border-sky-500 dark:from-sky-500 dark:via-sky-700 dark:to-sky-950 dark:shadow-sky-950/40"
			data-testid="content-membership-cta"
		>
			<p className="font-semibold text-white text-xl tracking-tight sm:text-2xl">
				{isPaused
					? "Resume membership to keep going"
					: "Unlock the full library"}
			</p>
			<p className="mt-2 max-w-xl text-sky-50/90 text-sm sm:text-base">
				Membership includes courses, exams, CEUs, and the full video library.
			</p>

			<div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
				<button
					type="button"
					onClick={startMembership}
					className="inline-flex w-full items-center justify-center rounded-full bg-white px-6 py-3 font-semibold text-base text-sky-800 shadow-md transition-all hover:bg-sky-50 hover:brightness-105 sm:w-auto"
				>
					{isPaused ? "Resume membership" : "Start membership"}
				</button>

				{!isSignedIn && (
					<button
						type="button"
						onClick={startFreeAccount}
						className="inline-flex w-full items-center justify-center rounded-full px-4 py-2 font-medium text-sky-100 text-sm underline-offset-2 transition-colors hover:text-white hover:underline sm:w-auto"
					>
						Create a free account instead
					</button>
				)}
			</div>
		</aside>
	);
};

export default ContentMembershipCTA;
